import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit, Trash2, X, Building2 } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

export default function AdminReferralSources() {
  const [sources, setSources] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const renderText = (text) => {
    if (!text) return "";
    if (typeof text === "string") return text;
    return text.en || text.ar || Object.values(text)[0] || "";
  };

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentSource, setCurrentSource] = useState(null);
  const [formData, setFormData] = useState({
    facility_name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    notes: ''
  });

  const currentUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    fetchSources();
  }, [page]);

  const fetchSources = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get('/admin/referral-sources', { params: { page } });
      setSources(data.data);
      setTotalPages(data.meta?.last_page || 1);
    } catch (error) {
      toast.error('Failed to load referral sources');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleOpenForm = (source = null) => {
    if (source) {
      setCurrentSource(source);
      setFormData({
        facility_name: source.facility_name || '',
        contact_person: source.contact_person || '',
        phone: source.phone || '',
        email: source.email || '',
        address: source.address || '',
        notes: source.notes || ''
      });
    } else {
      setCurrentSource(null);
      setFormData({
        facility_name: '', contact_person: '', phone: '', email: '', address: '', notes: ''
      });
    }
    setIsFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (currentSource) {
        await api.put(`/admin/referral-sources/${currentSource.id}`, formData);
        toast.success('Referral source updated');
      } else {
        await api.post('/admin/referral-sources', formData);
        toast.success('Referral source created');
      }
      setIsFormOpen(false);
      fetchSources();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving source');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this referral source? This may affect linked leads.')) return;
    try {
      await api.delete(`/admin/referral-sources/${id}`);
      toast.success('Referral source deleted');
      fetchSources();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error deleting source');
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Referral Sources</h1>
          <p className="text-gray-500 text-sm mt-1">Manage partner facilities and referrers</p>
        </div>
        
        {isAdmin && (
          <button
            onClick={() => handleOpenForm()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Plus size={20} />
            Add Source
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[1000px]">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-sm font-medium text-gray-500">
                <th className="px-6 py-4">Facility / Source</th>
                <th className="px-6 py-4">Contact Person</th>
                <th className="px-6 py-4">Contact Info</th>
                <th className="px-6 py-4 text-center">Total Leads</th>
                {isAdmin && <th className="px-6 py-4 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr><td colSpan={isAdmin ? 5 : 4} className="px-6 py-8 text-center text-gray-500">Loading...</td></tr>
              ) : sources.length === 0 ? (
                <tr><td colSpan={isAdmin ? 5 : 4} className="px-6 py-8 text-center text-gray-500">No referral sources found.</td></tr>
              ) : (
                sources.map((source) => (
                  <tr key={source.id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
                          <Building2 size={20} />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{renderText(source.facility_name)}</div>
                          {source.address && <div className="text-xs text-gray-500 truncate max-w-xs">{source.address}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600 text-sm">
                      {source.contact_person || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{source.phone || '-'}</div>
                      <div className="text-xs text-gray-500">{source.email || '-'}</div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center justify-center px-2.5 py-1 text-sm font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                        {source.leads_count || 0}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => handleOpenForm(source)} className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition" title="Edit">
                            <Edit size={18} />
                          </button>
                          <button onClick={() => handleDelete(source.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition" title="Delete">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 flex justify-between items-center bg-gray-50">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 bg-white border border-gray-200 rounded text-sm disabled:opacity-50">Previous</button>
            <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
            <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 bg-white border border-gray-200 rounded text-sm disabled:opacity-50">Next</button>
          </div>
        )}
      </div>

      {/* Form Modal */}
      <AnimatePresence>
        {isFormOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center z-10">
                <h2 className="text-xl font-bold text-gray-900">{currentSource ? 'Edit Source' : 'Add Source'}</h2>
                <button onClick={() => setIsFormOpen(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
                  <X size={20} />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">Facility / Source Name *</label>
                  <input required name="facility_name" value={formData.facility_name} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">Contact Person</label>
                  <input name="contact_person" value={formData.contact_person} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Phone</label>
                    <input name="phone" value={formData.phone} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Email</label>
                    <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">Address</label>
                  <input name="address" value={formData.address} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">Notes</label>
                  <textarea name="notes" rows={3} value={formData.notes} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"></textarea>
                </div>

                <div className="pt-4 flex justify-end gap-3 border-t border-gray-100">
                  <button type="button" onClick={() => setIsFormOpen(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition">
                    Cancel
                  </button>
                  <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition">
                    {currentSource ? 'Save Changes' : 'Create Source'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
